import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';
import TOUModal from '../components/TOUModal';
import AnnouncementModal from '../components/AnnouncementModal';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem('medprep_token'));
    const [touAccepted, setTouAccepted] = useState(true); // default true to avoid flash
    const [showAnnouncements, setShowAnnouncements] = useState(false);

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }
        
        try {
            const response = await authAPI.getMe();
            setUser(response.data);
            setTouAccepted(response.data.tou_accepted === true);
            if (response.data.tou_accepted === true) {
                setShowAnnouncements(true);
            }
        } catch (error) {
            console.error('Failed to fetch user:', error);
            localStorage.removeItem('medprep_token');
            localStorage.removeItem('medprep_user');
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password, rememberMe = false) => {
        const response = await authAPI.login({ email, password, remember_me: rememberMe });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('medprep_token', access_token);
        localStorage.setItem('medprep_user', JSON.stringify(userData));
        setToken(access_token);
        setUser(userData);
        setTouAccepted(userData.tou_accepted === true);
        if (userData.tou_accepted === true) {
            setShowAnnouncements(true);
        }
        
        return userData;
    };

    const register = async (email, password, name) => {
        const response = await authAPI.register({ email, password, name });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('medprep_token', access_token);
        localStorage.setItem('medprep_user', JSON.stringify(userData));
        setToken(access_token);
        setUser(userData);
        
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('medprep_token');
        localStorage.removeItem('medprep_user');
        setToken(null);
        setUser(null);
    };

    const handleTOUAccepted = () => {
        setTouAccepted(true);
        setUser(prev => prev ? { ...prev, tou_accepted: true } : prev);
        setShowAnnouncements(true);
    };

    const value = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
            {user && !touAccepted && <TOUModal onAccepted={handleTOUAccepted} />}
            {user && touAccepted && showAnnouncements && (
                <AnnouncementModal onDismissed={() => setShowAnnouncements(false)} />
            )}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
